import { call, put, takeLatest } from "redux-saga/effects";
import { getErrorMessageString } from "utils/utils";
import apiService from "services/apiservice";
import { notificationController } from "components/common/notificationController";
import { loadState } from "library/helpers/sessionStorage";
import {
  FETCH_ORDER_ENTRY_CUSTOMERS_REQUEST,
  FETCH_ORDER_ENTRY_VENDORS_REQUEST,
  ADD_UPDATE_ORDER_ENTRY_CUSTOMER_REQUEST,
  ADD_UPDATE_ORDER_ENTRY_VENDOR_REQUEST,
  FETCH_CUSTOMER_REFERENCES_REQUEST,
  FETCH_LOZIER_DIMENSIONS_REQUEST,
  FETCH_VENDORSERVICETYPES_REQUEST,
  ADD_UPDATE_VENDORSERVICETYPES_REQUEST,
} from "./actionTypes";
import {
  fetchCustomersSuccess,
  fetchCustomersFailure,
  fetchVendorsSuccess,
  fetchVendorsFailure,
  addUpdateCustomerSuccess,
  addUpdateCustomerFailure,
  addUpdateVendorSuccess,
  addUpdateVendorFailure,
  fetchCustomerReferencesSuccess,
  fetchCustomerReferencesFailure,
  fetchLozierDimensionsSuccess,
  fetchLozierDimensionsFailure,
  fetchVendorServiceTypesSuccess,
  fetchVendorServiceTypesFailure,
  addUpdateVendorServiceTypesSuccess,
  addUpdateVendorServiceTypesFailure,
} from "./actions";
import {
  Customers,
  Vendors,
  CustomerReference,
  WeightDimensions,
  VendorServiceTypes,
} from "./types";

const currentUser = loadState("loggedInUser");
const token = currentUser?.accessToken;

const postRequest = async (url: string, payload: any) => {
  const { data } = await apiService.post(url, payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

const getRequest = async (url: string) => {
  const { data } = await apiService.get(url, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

function* fetchCustomersSaga(action: any) {
  try {
    const response: Customers[] = yield call(
      postRequest,
      action.url,
      action.payload,
    );

    const totalCount = response.length > 0 ? response[0].totalCount : 0;
    yield put(fetchCustomersSuccess(response, totalCount));
  } catch (error: any) {
    const errorMessage = getErrorMessageString(error);
    yield put(fetchCustomersFailure(errorMessage));

    if (errorMessage && error?.response?.status !== 401) {
      notificationController.error({
        message: "Error",
        description: `Error fetching customer list: ${errorMessage}`,
      });
    }
  }
}

function* addUpdateCustomerSaga(action: any) {
  try {
    const response: { status: string; message: string } = yield call(
      postRequest,
      action.url,
      action.payload,
    );
    yield put(addUpdateCustomerSuccess(response.message));
    action.callback("Success", response.message);
  } catch (error: any) {
    const errorMessage = getErrorMessageString(error);
    yield put(addUpdateCustomerFailure(errorMessage));
    action.callback("Error", errorMessage);
  }
}

function* fetchVendorsSaga(action: any) {
  try {
    const response: Vendors[] = yield call(
      postRequest,
      action.url,
      action.payload,
    );

    const totalCount = response.length > 0 ? response[0].totalCount : 0;
    yield put(fetchVendorsSuccess(response, totalCount));
  } catch (error: any) {
    const errorMessage = getErrorMessageString(error);
    yield put(fetchVendorsFailure(errorMessage));

    if (errorMessage && error?.response?.status !== 401) {
      notificationController.error({
        message: "Error",
        description: `Error fetching vendor list: ${errorMessage}`,
      });
    }
  }
}

function* addUpdateVendorSaga(action: any) {
  try {
    const response: { status: string; message: string } = yield call(
      postRequest,
      action.url,
      action.payload,
    );
    yield put(addUpdateVendorSuccess(response.message));
    action.callback("Success", response.message);
  } catch (error: any) {
    const errorMessage = getErrorMessageString(error);
    yield put(addUpdateVendorFailure(errorMessage));
    action.callback("Error", errorMessage);
  }
}

function* fetchCustomerReferencesSaga(action: any) {
  try {
    const { customerId } = action.payload;
    const url = customerId ? `${action.url}/${customerId}` : action.url;

    const response: CustomerReference[] = yield call(getRequest, url);
    yield put(fetchCustomerReferencesSuccess(response));
  } catch (error: any) {
    const errorMessage = getErrorMessageString(error);
    yield put(fetchCustomerReferencesFailure(errorMessage));

    if (errorMessage && error?.response?.status !== 401) {
      notificationController.error({
        message: "Error",
        description: `Error fetching customer references: ${errorMessage}`,
      });
    }
  }
}

function* fetchLozierDimensionsSaga(action: any) {
  try {
    const url = action.url;

    const response: WeightDimensions[] = yield call(getRequest, url);
    yield put(fetchLozierDimensionsSuccess(response));
  } catch (error: any) {
    const errorMessage = getErrorMessageString(error);
    yield put(fetchLozierDimensionsFailure(errorMessage));

    if (errorMessage && error?.response?.status !== 401) {
      notificationController.error({
        message: "Error",
        description: `Error fetching lozier dimensions: ${errorMessage}`,
      });
    }
  }
}

function* fetchVendorServiceTypesSaga(action: any) {
  try {
    const response: VendorServiceTypes[] = yield call(
      postRequest,
      action.url,
      action.payload,
    );

    const totalCount = response.length > 0 ? response[0].totalCount : 0;
    yield put(fetchVendorServiceTypesSuccess(response, totalCount));
  } catch (error: any) {
    const errorMessage = getErrorMessageString(error);
    yield put(fetchVendorServiceTypesFailure(errorMessage));

    if (errorMessage && error?.response?.status !== 401) {
      notificationController.error({
        message: "Error",
        description: `Error fetching vendor service types list: ${errorMessage}`,
      });
    }
  }
}

function* addUpdateVendorServiceTypesSaga(action: any) {
  try {
    const response: { status: string; message: string } = yield call(
      postRequest,
      action.url,
      action.payload,
    );
    yield put(addUpdateVendorServiceTypesSuccess(response.message));
    action.callback("Success", response.message);
  } catch (error: any) {
    const errorMessage = getErrorMessageString(error);
    yield put(addUpdateVendorServiceTypesFailure(errorMessage));
    action.callback("Error", errorMessage);
  }
}

export default function* customersVendorsSaga() {
  yield takeLatest(FETCH_ORDER_ENTRY_CUSTOMERS_REQUEST, fetchCustomersSaga);
  yield takeLatest(
    ADD_UPDATE_ORDER_ENTRY_CUSTOMER_REQUEST,
    addUpdateCustomerSaga,
  );
  yield takeLatest(FETCH_ORDER_ENTRY_VENDORS_REQUEST, fetchVendorsSaga);
  yield takeLatest(ADD_UPDATE_ORDER_ENTRY_VENDOR_REQUEST, addUpdateVendorSaga);
  yield takeLatest(
    FETCH_CUSTOMER_REFERENCES_REQUEST,
    fetchCustomerReferencesSaga,
  );
  yield takeLatest(FETCH_LOZIER_DIMENSIONS_REQUEST, fetchLozierDimensionsSaga);
  yield takeLatest(
    FETCH_VENDORSERVICETYPES_REQUEST,
    fetchVendorServiceTypesSaga,
  );
  yield takeLatest(
    ADD_UPDATE_VENDORSERVICETYPES_REQUEST,
    addUpdateVendorServiceTypesSaga,
  );
}
